// src/components/admin/AdminCompliance.js
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';
import { writeAuditLog, invalidateRulesCache, invalidateBlocksCache } from '../../lib/compliance';

const TABS = ['overview', 'product_rules', 'country_blocks', 'age_gate', 'audit_log'];
const TAB_LABELS = {
  overview:       '📊 Overview',
  product_rules:  '📦 Product Rules',
  country_blocks: '🚫 Country Blocks',
  age_gate:       '🔞 Age Gate',
  audit_log:      '📋 Audit Log',
};

const RULE_TYPES = [
  { value: 'blocked',    label: 'Blocked',    color: '#e05050', desc: 'Product unavailable in this country' },
  { value: 'restricted', label: 'Restricted', color: '#f0a500', desc: 'Available with additional warnings' },
  { value: 'allowed',    label: 'Allowed',    color: '#4cca74', desc: 'Explicitly permitted (overrides defaults)' },
];

const COUNTRY_OPTIONS = ['DE','SK','HU','RO','PL','AT','NL','HR'];

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color = '#f4f4f4', sub }) {
  return (
    <div style={{ background: '#111', borderRadius: 14, padding: '18px 20px', border: '1px solid rgba(255,255,255,.07)' }}>
      <div style={{ fontSize: '.7rem', color: '#555', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.04em', color }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: '.72rem', color: '#555', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AdminCompliance() {
  const { profile, user } = useApp();
  const actor = { id: user?.id, email: profile?.email || user?.email };

  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Data state
  const [summary, setSummary]         = useState(null);
  const [productRules, setProductRules] = useState([]);
  const [countryBlocks, setCountryBlocks] = useState([]);
  const [ageGateEvents, setAgeGateEvents] = useState([]);
  const [auditLog, setAuditLog]       = useState([]);
  const [products, setProducts]       = useState([]);

  // Form state
  const [ruleForm, setRuleForm] = useState({ product_id: '', country_code: 'DE', rule_type: 'blocked', reason: '', internal_note: '' });
  const [blockForm, setBlockForm] = useState({ country_code: '', reason: '' });
  const [showRuleForm, setShowRuleForm]   = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const { showToast } = useApp();

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [summRes, rulesRes, blocksRes, ageRes, auditRes, prodRes] = await Promise.all([
      supabase.from('compliance_summary').select('*').single(),
      supabase.from('product_country_rules').select('*, products(name)').order('created_at', { ascending: false }),
      supabase.from('country_block_list').select('*').order('created_at', { ascending: false }),
      supabase.from('age_gate_events').select('*').order('created_at', { ascending: false }).limit(100),
      supabase.from('audit_log').select('*').order('created_at', { ascending: false }).limit(200),
      supabase.from('products').select('id, name').eq('active', true).order('sort_order'),
    ]);
    setSummary(summRes.data);
    setProductRules(rulesRes.data || []);
    setCountryBlocks(blocksRes.data || []);
    setAgeGateEvents(ageRes.data || []);
    setAuditLog(auditRes.data || []);
    setProducts(prodRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Save product rule ─────────────────────────────────────────────────────
  const saveRule = async () => {
    if (!ruleForm.product_id || !ruleForm.country_code) {
      showToast('Select a product and country', 'error'); return;
    }
    setSaving(true);
    const payload = {
      product_id:    ruleForm.product_id,
      country_code:  ruleForm.country_code,
      rule_type:     ruleForm.rule_type,
      reason:        ruleForm.reason || null,
      internal_note: ruleForm.internal_note || null,
      created_by:    user?.id,
    };
    const { error } = await supabase.from('product_country_rules')
      .upsert(payload, { onConflict: 'product_id,country_code' });

    if (error) { showToast('Save failed: ' + error.message, 'error'); setSaving(false); return; }

    await writeAuditLog(actor, 'country_rule.create', 'product_country_rule',
      `${ruleForm.product_id}::${ruleForm.country_code}`, null, payload,
      `Rule: ${ruleForm.rule_type} for ${ruleForm.country_code}`);

    invalidateRulesCache();
    showToast('Rule saved', 'success');
    setShowRuleForm(false);
    setRuleForm({ product_id: '', country_code: 'DE', rule_type: 'blocked', reason: '', internal_note: '' });
    setSaving(false);
    loadAll();
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete ${rule.rule_type} rule for ${rule.country_code}?`)) return;
    await supabase.from('product_country_rules').delete().eq('id', rule.id);
    await writeAuditLog(actor, 'country_rule.delete', 'product_country_rule',
      `${rule.product_id}::${rule.country_code}`, rule, null);
    invalidateRulesCache();
    showToast('Rule deleted', 'info');
    loadAll();
  };

  // ── Save country block ────────────────────────────────────────────────────
  const saveBlock = async () => {
    if (!blockForm.country_code || !blockForm.reason) {
      showToast('Country code and reason required', 'error'); return;
    }
    setSaving(true);
    const payload = { country_code: blockForm.country_code.toUpperCase(), reason: blockForm.reason, active: true, created_by: user?.id };
    const { error } = await supabase.from('country_block_list').upsert(payload, { onConflict: 'country_code' });
    if (error) { showToast('Save failed: ' + error.message, 'error'); setSaving(false); return; }
    await writeAuditLog(actor, 'country_block.create', 'country_block_list', blockForm.country_code, null, payload);
    invalidateBlocksCache();
    showToast('Country block saved', 'success');
    setShowBlockForm(false);
    setBlockForm({ country_code: '', reason: '' });
    setSaving(false);
    loadAll();
  };

  const toggleBlock = async (block) => {
    const before = { ...block };
    const after  = { ...block, active: !block.active };
    await supabase.from('country_block_list').update({ active: !block.active }).eq('country_code', block.country_code);
    await writeAuditLog(actor, 'country_block.toggle', 'country_block_list', block.country_code, before, after);
    invalidateBlocksCache();
    showToast(block.active ? 'Block disabled' : 'Block enabled', 'info');
    loadAll();
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const ruleColor = (type) => RULE_TYPES.find(r => r.value === type)?.color || '#888';
  const productName = (id) => {
    const p = products.find(x => x.id === id);
    return p?.name?.en || id?.slice(0, 8) || '—';
  };
  const fmtDate = (ts) => ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const passRate = ageGateEvents.length > 0
    ? Math.round((ageGateEvents.filter(e => e.result === 'passed').length / ageGateEvents.length) * 100)
    : 0;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ padding: '32px 24px' }}>
      <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em', marginBottom: 4 }}>
        Compliance
      </h2>
      <p style={{ fontSize: '.8rem', color: '#555', marginBottom: 20 }}>
        Country restrictions, age gate logs, and immutable audit trail.
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '7px 14px', border: 'none', borderRadius: 20,
            background: tab === t ? 'rgba(76,202,116,.15)' : '#111',
            color: tab === t ? '#4cca74' : '#555',
            fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem',
            fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
            <StatCard label="Blocked Rules"      value={summary?.blocked_rules}        color="#e05050" />
            <StatCard label="Restricted Rules"   value={summary?.restricted_rules}     color="#f0a500" />
            <StatCard label="Blocked Countries"  value={summary?.blocked_countries}    color="#e05050" />
            <StatCard label="Age Gates (24h)"    value={summary?.age_gates_24h}        sub={`${summary?.age_gates_passed_24h} passed`} />
            <StatCard label="Compliance Events"  value={summary?.compliance_events_24h} color="#f0a500" sub="last 24h" />
            <StatCard label="Audit Entries"      value={summary?.audit_entries_24h}    sub="last 24h" />
          </div>

          {/* Recent compliance events */}
          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#f0a500' }}>
              Recent Compliance Events
            </div>
            {!ageGateEvents.length && !auditLog.length ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '.82rem' }}>No events yet</div>
            ) : auditLog.slice(0, 8).map(entry => (
              <div key={entry.id} style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, color: '#ccc' }}>{entry.action}</span>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.68rem', color: '#555', background: 'rgba(255,255,255,.05)', padding: '1px 8px', borderRadius: 10 }}>{entry.entity_type}</span>
                  </div>
                  <div style={{ fontSize: '.72rem', color: '#555', marginTop: 2 }}>{entry.actor_email} · {fmtDate(entry.created_at)}</div>
                  {entry.notes && <div style={{ fontSize: '.72rem', color: '#444', marginTop: 2 }}>{entry.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── PRODUCT RULES ── */}
      {tab === 'product_rules' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            {!showRuleForm && (
              <button className="btn-primary" onClick={() => setShowRuleForm(true)} style={{ minHeight: 40 }}>
                + Add Rule
              </button>
            )}
          </div>

          {showRuleForm && (
            <div style={{ background: '#0d0d0d', border: '1px solid rgba(76,202,116,.2)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <div>
                  <div className="input-label">Product *</div>
                  <select className="input-field" value={ruleForm.product_id} onChange={e => setRuleForm(f => ({ ...f, product_id: e.target.value }))} style={{ appearance: 'none' }}>
                    <option value="">Select product…</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name?.en || p.id}</option>)}
                  </select>
                </div>
                <div>
                  <div className="input-label">Country *</div>
                  <select className="input-field" value={ruleForm.country_code} onChange={e => setRuleForm(f => ({ ...f, country_code: e.target.value }))} style={{ appearance: 'none' }}>
                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <div className="input-label">Rule Type *</div>
                  <select className="input-field" value={ruleForm.rule_type} onChange={e => setRuleForm(f => ({ ...f, rule_type: e.target.value }))} style={{ appearance: 'none' }}>
                    {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                  </select>
                </div>
                <div>
                  <div className="input-label">User-facing Reason</div>
                  <input className="input-field" value={ruleForm.reason} onChange={e => setRuleForm(f => ({ ...f, reason: e.target.value }))} placeholder="Not available in your region" />
                </div>
                <div>
                  <div className="input-label">Internal Note (admin only)</div>
                  <input className="input-field" value={ruleForm.internal_note} onChange={e => setRuleForm(f => ({ ...f, internal_note: e.target.value }))} placeholder="Legal basis, date of decision…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn-primary" onClick={saveRule} disabled={saving} style={{ minHeight: 40 }}>
                  {saving ? 'Saving…' : 'Save Rule'}
                </button>
                <button onClick={() => setShowRuleForm(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 25, padding: '8px 18px', color: '#555', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
            {productRules.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '.82rem' }}>No rules configured yet.</div>
            )}
            {productRules.map(rule => (
              <div key={rule.id} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: ruleColor(rule.rule_type), flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, color: '#f4f4f4' }}>
                      {productName(rule.product_id)}
                    </span>
                    <span style={{ fontSize: '.7rem', color: '#555' }}>→</span>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, color: '#555' }}>{rule.country_code}</span>
                    <span style={{ background: `${ruleColor(rule.rule_type)}20`, color: ruleColor(rule.rule_type), fontSize: '.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, fontFamily: "'Barlow Condensed',sans-serif", textTransform: 'uppercase' }}>
                      {rule.rule_type}
                    </span>
                  </div>
                  {rule.reason && <div style={{ fontSize: '.75rem', color: '#666', marginTop: 2 }}>{rule.reason}</div>}
                  {rule.internal_note && <div style={{ fontSize: '.68rem', color: '#444', marginTop: 1 }}>🔒 {rule.internal_note}</div>}
                  <div style={{ fontSize: '.68rem', color: '#333', marginTop: 2 }}>{fmtDate(rule.created_at)}</div>
                </div>
                <button onClick={() => deleteRule(rule)} style={{ background: 'none', border: '1px solid rgba(224,80,80,.25)', borderRadius: 20, padding: '5px 12px', color: '#e05050', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── COUNTRY BLOCKS ── */}
      {tab === 'country_blocks' && (
        <>
          <div style={{ background: 'rgba(240,165,0,.06)', border: '1px solid rgba(240,165,0,.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: '.8rem', color: '#f0a500', lineHeight: 1.7 }}>
            ⚠️ Country blocks prevent ALL orders from a country. Use product rules for per-item restrictions.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            {!showBlockForm && (
              <button className="btn-primary" onClick={() => setShowBlockForm(true)} style={{ minHeight: 40 }}>
                + Block Country
              </button>
            )}
          </div>

          {showBlockForm && (
            <div style={{ background: '#0d0d0d', border: '1px solid rgba(224,80,80,.2)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <div>
                  <div className="input-label">Country Code *</div>
                  <input className="input-field" value={blockForm.country_code} onChange={e => setBlockForm(f => ({ ...f, country_code: e.target.value.toUpperCase() }))} placeholder="DE" maxLength={2} style={{ textTransform: 'uppercase' }} />
                </div>
                <div>
                  <div className="input-label">Reason *</div>
                  <input className="input-field" value={blockForm.reason} onChange={e => setBlockForm(f => ({ ...f, reason: e.target.value }))} placeholder="Not available in this region" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn-primary" onClick={saveBlock} disabled={saving} style={{ minHeight: 40, background: '#e05050' }}>
                  {saving ? 'Saving…' : 'Block Country'}
                </button>
                <button onClick={() => setShowBlockForm(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 25, padding: '8px 18px', color: '#555', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
            {countryBlocks.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '.82rem' }}>No country blocks active.</div>
            )}
            {countryBlocks.map(block => (
              <div key={block.country_code} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', gap: 12, opacity: block.active ? 1 : .45 }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.4rem', color: block.active ? '#e05050' : '#555', minWidth: 36 }}>{block.country_code}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '.82rem', color: '#ccc' }}>{block.reason}</div>
                  <div style={{ fontSize: '.68rem', color: '#444', marginTop: 2 }}>{fmtDate(block.created_at)}</div>
                </div>
                <button onClick={() => toggleBlock(block)} style={{ background: 'none', border: `1px solid ${block.active ? 'rgba(240,165,0,.3)' : 'rgba(76,202,116,.3)'}`, borderRadius: 20, padding: '6px 12px', color: block.active ? '#f0a500' : '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer' }}>
                  {block.active ? 'Disable' : 'Enable'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── AGE GATE ── */}
      {tab === 'age_gate' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Total Events"   value={ageGateEvents.length} sub="last 100" />
            <StatCard label="Pass Rate"      value={`${passRate}%`}        color="#4cca74" />
            <StatCard label="Passed"         value={ageGateEvents.filter(e => e.result === 'passed').length}  color="#4cca74" />
            <StatCard label="Exited"         value={ageGateEvents.filter(e => e.result === 'exited').length}  color="#e05050" />
          </div>

          {/* Country breakdown */}
          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', padding: 20, marginBottom: 16 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 14 }}>By Country</div>
            {Object.entries(
              ageGateEvents.reduce((acc, e) => { acc[e.country] = (acc[e.country] || 0) + 1; return acc; }, {})
            ).sort(([,a],[,b]) => b - a).slice(0, 8).map(([country, count]) => (
              <div key={country} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, color: '#ccc', minWidth: 28 }}>{country}</span>
                <div style={{ flex: 1, height: 5, background: '#1a1a1a', borderRadius: 3 }}>
                  <div style={{ height: '100%', background: '#4cca74', borderRadius: 3, width: `${(count / ageGateEvents.length) * 100}%` }} />
                </div>
                <span style={{ fontSize: '.75rem', color: '#555', minWidth: 28, textAlign: 'right' }}>{count}</span>
              </div>
            ))}
          </div>

          {/* Event table */}
          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74' }}>
              Recent Events (100)
            </div>
            {ageGateEvents.slice(0, 50).map(e => (
              <div key={e.id} style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.result === 'passed' ? '#4cca74' : '#e05050', flexShrink: 0, display: 'inline-block' }} />
                <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', color: e.result === 'passed' ? '#4cca74' : '#e05050', fontWeight: 700, minWidth: 55 }}>{e.result}</span>
                <span style={{ fontSize: '.75rem', color: '#666', minWidth: 28 }}>{e.country}</span>
                <span style={{ fontSize: '.72rem', color: '#444', flex: 1 }}>{e.session_id?.slice(0, 14)}…</span>
                <span style={{ fontSize: '.68rem', color: '#333' }}>{fmtDate(e.created_at)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── AUDIT LOG ── */}
      {tab === 'audit_log' && (
        <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74' }}>
              Immutable Audit Trail ({auditLog.length} entries)
            </div>
            <span style={{ fontSize: '.68rem', color: '#444' }}>Read-only — no edits or deletes possible</span>
          </div>
          {auditLog.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#444', fontSize: '.82rem' }}>No audit entries yet.</div>
          )}
          {auditLog.map(entry => (
            <div key={entry.id} style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.85rem', fontWeight: 700, color: '#f4f4f4' }}>{entry.action}</span>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.68rem', color: '#555', background: 'rgba(255,255,255,.05)', padding: '1px 8px', borderRadius: 10 }}>{entry.entity_type}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '.65rem', color: '#444' }}>{entry.entity_id?.slice(0, 12)}…</span>
                  </div>
                  <div style={{ fontSize: '.72rem', color: '#555' }}>
                    {entry.actor_email} · {fmtDate(entry.created_at)}
                    {entry.notes && <span style={{ color: '#444' }}> · {entry.notes}</span>}
                  </div>
                  {/* Before/after diff preview */}
                  {(entry.before_json || entry.after_json) && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      {entry.before_json && (
                        <div style={{ background: 'rgba(224,80,80,.06)', border: '1px solid rgba(224,80,80,.15)', borderRadius: 6, padding: '4px 8px', flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '.6rem', color: '#e05050', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, marginBottom: 2 }}>BEFORE</div>
                          <pre style={{ fontSize: '.62rem', color: '#666', margin: 0, overflow: 'hidden', maxHeight: 60, textOverflow: 'ellipsis' }}>
                            {JSON.stringify(entry.before_json, null, 1).slice(0, 200)}
                          </pre>
                        </div>
                      )}
                      {entry.after_json && (
                        <div style={{ background: 'rgba(76,202,116,.04)', border: '1px solid rgba(76,202,116,.12)', borderRadius: 6, padding: '4px 8px', flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '.6rem', color: '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, marginBottom: 2 }}>AFTER</div>
                          <pre style={{ fontSize: '.62rem', color: '#666', margin: 0, overflow: 'hidden', maxHeight: 60, textOverflow: 'ellipsis' }}>
                            {JSON.stringify(entry.after_json, null, 1).slice(0, 200)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
